import React from "react";
import { motion, type HTMLMotionProps } from "framer-motion";
import { cn } from "../../lib/cn";

type ButtonVariant = "primary" | "secondary" | "ghost" | "destructive";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps
  extends Omit<HTMLMotionProps<"button">, "ref" | "size"> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  iconOnly?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  loading?: boolean;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary: [
    "bg-[var(--color-lever-orange)] text-white",
    "hover:brightness-110 hover:shadow-[var(--shadow-md)]",
    "focus-visible:ring-[var(--color-cool-azure)]",
  ].join(" "),
  secondary: [
    "border border-[var(--border-default)] text-[var(--text-primary)]",
    "bg-[var(--bg-surface)] backdrop-blur-[var(--blur-sm)]",
    "hover:bg-[var(--bg-surface-hover)] hover:shadow-[var(--shadow-sm)]",
    "focus-visible:ring-[var(--color-cool-azure)]",
  ].join(" "),
  ghost: [
    "text-[var(--text-secondary)]",
    "hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]",
    "focus-visible:ring-[var(--color-cool-azure)]",
  ].join(" "),
  destructive: [
    "bg-[var(--color-soft-red)]/10 text-[var(--color-soft-red)]",
    "border border-[var(--color-soft-red)]/20",
    "hover:bg-[var(--color-soft-red)]/20 hover:shadow-[var(--glow-error)]",
    "focus-visible:ring-[var(--color-soft-red)]",
  ].join(" "),
};

const sizeStyles: Record<ButtonSize, string> = {
  sm: "h-8 px-3 text-xs gap-1.5 rounded-[var(--radius-sm)]",
  md: "h-9 px-4 text-sm gap-2 rounded-[var(--radius-md)]",
  lg: "h-11 px-5 text-base gap-2.5 rounded-[var(--radius-lg)]",
};

const iconOnlySizeStyles: Record<ButtonSize, string> = {
  sm: "h-8 w-8 rounded-[var(--radius-sm)]",
  md: "h-9 w-9 rounded-[var(--radius-md)]",
  lg: "h-11 w-11 rounded-[var(--radius-lg)]",
};

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      iconOnly = false,
      leftIcon,
      rightIcon,
      loading = false,
      disabled,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const isDisabled = disabled || loading;

    return (
      <motion.button
        ref={ref}
        disabled={isDisabled}
        whileTap={isDisabled ? undefined : { scale: 0.98 }}
        transition={{
          type: "spring",
          stiffness: 500,
          damping: 30,
          duration: 0.12,
        }}
        className={cn(
          "inline-flex items-center justify-center font-medium",
          "select-none whitespace-nowrap",
          "transition-[background-color,border-color,color,opacity,box-shadow] duration-[var(--duration-standard)] ease-[var(--easing-default)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-opacity-50",
          "disabled:pointer-events-none disabled:opacity-50",
          variantStyles[variant],
          iconOnly ? iconOnlySizeStyles[size] : sizeStyles[size],
          className,
        )}
        {...props}
      >
        {loading ? (
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
        ) : (
          <>
            {leftIcon && (
              <span className="inline-flex shrink-0">{leftIcon}</span>
            )}
            {!iconOnly && children}
            {rightIcon && (
              <span className="inline-flex shrink-0">{rightIcon}</span>
            )}
          </>
        )}
      </motion.button>
    );
  },
);

Button.displayName = "Button";

export { Button };
export type { ButtonProps, ButtonVariant, ButtonSize };
